// src/hooks/useImportCosts.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CostService } from '../admin/pages/import-cost/importCostService';
import { CostFormData } from '../admin/pages/import-cost/importCost';

export const COST_KEYS = {
  all: ['costs'] as const,
  lists: () => ['costs', 'list'] as const,
  paginated: (page: number, search: string, category: string, type: string, status: string) =>
    ['costs', 'list', page, search, category, type, status] as const,
  stats: () => ['costs', 'stats'] as const,
};

export interface CostPaginationParams {
  page: number;
  search: string;
  category: string;
  type: string;
  status: string;
  pageSize: number;
}

export const useCostsPaginated = (params: CostPaginationParams) => {
  return useQuery({
    queryKey: COST_KEYS.paginated(params.page, params.search, params.category, params.type, params.status),
    queryFn: () =>
      CostService.getCostsPaginated(
        params.pageSize,
        params.page,
        params.search || undefined,
        params.category !== 'all' ? params.category : undefined,
        params.type !== 'all' ? params.type : undefined,
        params.status !== 'all' ? params.status : undefined
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: (prev: any) => prev,
  });
};

export const useCostStats = () => {
  return useQuery({
    queryKey: COST_KEYS.stats(),
    queryFn: () => CostService.getCostStats(),
    staleTime: 1000 * 60 * 5,
  });
};

export interface CreateCostArgs {
  formData: CostFormData;
  userId: string;
  userName: string;
}

export interface UpdateCostArgs {
  costId: string;
  formData: CostFormData;
}

export interface CostMutationResult {
  success: boolean;
  message?: string;
  id?: string;
}

export const useCostMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: COST_KEYS.all });
  };

  const create = useMutation<CostMutationResult, Error, CreateCostArgs>({
    mutationFn: async ({ formData, userId, userName }) => {
      const id = await CostService.createCost(formData, userId, userName);
      return { success: true, id };
    },
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error creating cost:', error);
    },
  });

  const update = useMutation<CostMutationResult, Error, UpdateCostArgs>({
    mutationFn: async ({ costId, formData }) => {
      await CostService.updateCost(costId, formData);
      return { success: true, id: costId };
    },
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error updating cost:', error);
    },
  });

  const remove = useMutation<void, Error, string>({
    mutationFn: (id: string) => CostService.deleteCost(id),
    onSuccess: invalidateAll,
    onError: (error) => {
      console.error('Error deleting cost:', error);
    },
  });

  return { create, update, remove };
};
